/**
 * Multi-factor DEMA-WMA Gainer: editable settings sections (SectionCard), field validation and the plain-text
 * "rules in effect" summary. Shared by the page and its Rules in Effect tab. Saving a section creates a NEW
 * active config version on the server (validated there -- a rejected save changes nothing).
 */
import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  FormControlLabel,
  FormGroup,
  Grid,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { type DemaWmaFieldValue, type DemaWmaOverview } from "@/services/demaWmaGainerService";
import { useDemaWmaService } from "@/services/demaWmaServiceContext";
import { extractErrorMessage } from "@/utils/errors";

type Kind = "int" | "num" | "select" | "bool" | "time" | "instruments";

export interface FieldSpec {
  key: string;
  label: string;
  kind: Kind;
  help: string;
  options?: { value: string; label: string }[];
  optional?: boolean;
  min?: number;
  max?: number;
}

export interface SectionSpec {
  id: string;
  title: string;
  description: string;
  fields: FieldSpec[];
}

export const SECTIONS: SectionSpec[] = [
  {
    id: "indicators",
    title: "Instruments & indicators",
    description: "Signals are computed on completed index bars of the active version's timeframe (shown at the top of this section). DEMA uses the index close; the RSI WMA is the WMA of RSI (Hilega Milega).",
    fields: [
      { key: "instruments", label: "Indices", kind: "instruments", help: "Each index is evaluated independently" },
      { key: "dema_fast", label: "DEMA fast length", kind: "int", min: 2, max: 500, help: "Default 10" },
      { key: "dema_slow", label: "DEMA slow length", kind: "int", min: 2, max: 500, help: "Default 100" },
      { key: "rsi_period", label: "RSI period", kind: "int", min: 2, max: 100, help: "Default 9" },
      { key: "rsi_wma", label: "WMA of RSI length", kind: "int", min: 2, max: 100, help: "Default 21" },
      { key: "warmup_bars", label: "Warm-up history (bars)", kind: "int", min: 50, max: 5000, help: "At least 2 × slow length" },
    ],
  },
  {
    id: "rules",
    title: "Entry & exit rules",
    description:
      "CE: DEMA fast crosses above DEMA slow with WMA21 rising, exit when WMA21 turns down. PE: the mirror. Plus % stop-loss and % target on the option premium.",
    fields: [
      { key: "stop_loss_pct", label: "Stop-loss (% of premium)", kind: "num", min: 0, max: 100, help: "Exit when the option falls this much" },
      { key: "target_pct", label: "Target (% of premium)", kind: "num", min: 0, max: 1000, help: "Exit when the option gains this much" },
      {
        key: "price_source", label: "SL / target checked on", kind: "select", help: "Live option price or 5-minute close",
        options: [{ value: "TICK", label: "Every option tick" }, { value: "CANDLE_CLOSE", label: "5-minute option close" }],
      },
      {
        key: "flat_wma_action", label: "WMA21 exactly flat", kind: "select", help: "Current WMA21 equals previous",
        options: [{ value: "HOLD", label: "Hold the position" }, { value: "EXIT", label: "Exit" }],
      },
      { key: "reverse_on_opposite", label: "Reverse on opposite signal", kind: "bool", help: "Off: exit only; the next entry needs a new cross" },
      { key: "min_dte", label: "Minimum days to expiry", kind: "int", min: 0, max: 10, help: "1 = roll to the next expiry on expiry day" },
    ],
  },
  {
    id: "session",
    title: "Trading window",
    description: "Intraday only. Entries only on bars closing inside window 1 or the optional window 2; open positions are held through the gap between windows and every open position is squared off at the square-off time (IST).",
    fields: [
      { key: "entry_start", label: "Window 1: entries from", kind: "time", help: "IST" },
      { key: "entry_end", label: "Window 1: entries until", kind: "time", help: "IST" },
      { key: "square_off", label: "Square-off", kind: "time", help: "IST" },
      { key: "entry_start_2", label: "Window 2: entries from", kind: "time", optional: true, help: "Optional; IST; empty both = one window" },
      { key: "entry_end_2", label: "Window 2: entries until", kind: "time", optional: true, help: "Optional; IST; before square-off" },
    ],
  },
  {
    id: "limits",
    title: "Limits & capital",
    description: "Checked before every entry; exits are never blocked. Trades are sized down to fit capital and risk, or skipped below one lot.",
    fields: [
      { key: "lots", label: "Lots per trade", kind: "int", min: 1, max: 50, help: "× exchange lot size" },
      { key: "max_trades_per_day", label: "Max trades per day (per index)", kind: "int", min: 1, max: 20, help: "Entries stop after this many" },
      { key: "max_open_positions", label: "Max open positions (total)", kind: "int", min: 1, max: 2, help: "At most one per index" },
      { key: "allocated_capital", label: "Allocated capital (₹)", kind: "num", min: 0, help: "Premium in open positions must fit" },
      { key: "max_daily_loss", label: "Max daily loss (₹)", kind: "num", min: 0, help: "Realized + open loss that halts entries" },
      { key: "max_exposure_pct", label: "Max exposure (% of capital)", kind: "num", min: 0, max: 100, optional: true, help: "Optional; empty = no extra cap" },
      { key: "max_risk_per_trade", label: "Max risk per trade (₹)", kind: "num", min: 0, optional: true, help: "Optional; entry to SL × quantity" },
    ],
  },
  {
    id: "execution",
    title: "Execution",
    description: "How orders are priced. Paper fills use the live ask (buy) and bid (sell) plus the paper slippage, only inside the limit band.",
    fields: [
      {
        key: "order_type", label: "Order type", kind: "select", help: "Marketable limit stays inside the band",
        options: [{ value: "MARKETABLE_LIMIT", label: "Marketable limit" }, { value: "MARKET", label: "Market" }],
      },
      { key: "limit_band_pct", label: "Limit band (%)", kind: "num", min: 0, max: 20, help: "Max distance from the quote" },
      { key: "paper_slippage_pct", label: "Paper slippage (%)", kind: "num", min: 0, max: 10, help: "Applied to every paper fill" },
      { key: "max_quote_age_seconds", label: "Max quote age (s)", kind: "int", min: 1, max: 600, help: "Older quote = no order" },
    ],
  },
];

/** Sections adapted to the active rule set. v1 is the original SECTIONS; v2 has its own rule text and its
 *  stop-loss / target are optional (v2 is stored with no target). Other sections are identical. */
export function sectionsFor(ruleSet?: string): SectionSpec[] {
  if (ruleSet === RULE_SET_V6) {
    return SECTIONS.map((s) => {
      if (s.id === "rules") {
        return {
          ...s,
          description:
            "v6 (CE and PE): enters while flat when every entry condition in the summary above holds; exits on the exit rule in the summary (an opposite signal only exits) or on the stop-loss / target below. The exact thresholds of this version (gap, slopes, exit WMA) are the ones listed in the summary above; they are fixed by the saved version and cannot be edited here.",
          fields: s.fields.map((f) => {
            if (f.key === "stop_loss_pct") return { ...f, optional: true, help: "Optional; empty = no stop-loss" };
            if (f.key === "target_pct") return { ...f, optional: true, help: "Optional; empty = no target (v6 default)" };
            if (f.key === "reverse_on_opposite") return { ...f, help: "v6: opposite signal = exit only; must stay off" };
            if (f.key === "flat_wma_action") return { ...f, help: "v6: a flat WMA21 holds" };
            return f;
          }),
        };
      }
      if (s.id === "limits") {
        return {
          ...s,
          fields: s.fields.map((f) =>
            f.key === "max_trades_per_day" ? { ...f, optional: true, help: "Optional; empty = unlimited (v6 default)" } : f),
        };
      }
      return s;
    });
  }
  if (ruleSet !== RULE_SET_V2) return SECTIONS;
  return SECTIONS.map((s) => {
    if (s.id !== "rules") return s;
    return {
      ...s,
      description:
        "v2 (CE-only): enters on the first bar of a new setup (DEMA fast above DEMA slow by the gap, DEMA slow not falling, close above DEMA slow, WMA21 rising); exits when WMA21 turns strictly down. Stop-loss and target are on the option premium; v2 is stored with a stop-loss and no target. Entry-rule values (gap points, same-session slopes, re-entry after a fresh cross) are fixed by the rule set.",
      fields: s.fields.map((f) => {
        if (f.key === "stop_loss_pct") return { ...f, optional: true, help: "Optional; empty = no stop-loss" };
        if (f.key === "target_pct") return { ...f, optional: true, help: "Optional; empty = no target (v2 default)" };
        if (f.key === "reverse_on_opposite") return { ...f, help: "v2 is CE-only: must stay off" };
        if (f.key === "flat_wma_action") return { ...f, help: "v2: a flat WMA21 holds" };
        return f;
      }),
    };
  });
}

type FormValue = string | boolean | string[];

function toForm(v: DemaWmaFieldValue | undefined, kind: Kind): FormValue {
  if (kind === "bool") return Boolean(v);
  if (kind === "instruments") return Array.isArray(v) ? v : [];
  return v === null || v === undefined ? "" : String(v);
}

export function fieldError(f: FieldSpec, v: FormValue): string | null {
  if (f.kind === "instruments") return (v as string[]).length === 0 ? "Pick at least one" : null;
  if (f.kind === "bool" || f.kind === "select") return null;
  const raw = String(v).trim();
  if (raw === "") return f.optional ? null : "Required";
  if (f.kind === "time") return /^([01]\d|2[0-3]):[0-5]\d$/.test(raw) ? null : "HH:MM";
  const n = Number(raw);
  if (!Number.isFinite(n)) return "Number";
  if (f.kind === "int" && !Number.isInteger(n)) return "Whole number";
  if (f.min !== undefined && (f.kind === "int" ? n < f.min : n <= f.min)) return f.kind === "int" ? `At least ${f.min}` : `Greater than ${f.min}`;
  if (f.max !== undefined && n > f.max) return `At most ${f.max}`;
  return null;
}

function toPayload(f: FieldSpec, v: FormValue): DemaWmaFieldValue {
  if (f.kind === "bool" || f.kind === "instruments") return v as boolean | string[];
  if (f.kind === "time" && f.optional && String(v).trim() === "") return null;
  if (f.kind === "select" || f.kind === "time") return String(v);
  const raw = String(v).trim();
  return raw === "" ? null : Number(raw);
}

function same(a: FormValue, b: FormValue): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

export function SectionCard({ section, overview, onSaved }: { section: SectionSpec; overview: DemaWmaOverview; onSaved: (msg: string) => Promise<void> }) {
  const demaWmaGainerService = useDemaWmaService();
  const initial = useMemo(
    () => Object.fromEntries(section.fields.map((f) => [f.key, toForm(overview.fields[f.key], f.kind)])) as Record<string, FormValue>,
    [overview, section],
  );
  const [values, setValues] = useState<Record<string, FormValue>>(initial);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => setValues(initial), [initial]);

  const errors = Object.fromEntries(section.fields.map((f) => [f.key, fieldError(f, values[f.key])]));
  const invalid = Object.values(errors).some((e) => e !== null);
  const dirtyKeys = section.fields.filter((f) => !same(values[f.key], initial[f.key])).map((f) => f.key);

  const save = async () => {
    setSaving(true);
    setErr(null);
    try {
      const payload = Object.fromEntries(
        section.fields.filter((f) => dirtyKeys.includes(f.key)).map((f) => [f.key, toPayload(f, values[f.key])]),
      );
      const res = await demaWmaGainerService.saveConfig(payload);
      await onSaved(`${section.title}: saved as config v${res.version}.`);
    } catch (e) {
      setErr(extractErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card variant="outlined" sx={{ mb: 2 }} data-testid={`section-${section.id}`}>
      <CardContent>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>{section.title}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{section.description}</Typography>
        <Grid container spacing={2}>
          {section.fields.map((f) => {
            const v = values[f.key];
            const fe = errors[f.key];
            const suggested = overview.suggested_fields.includes(f.key) && !dirtyKeys.includes(f.key);
            const helper = (
              <>
                {fe ?? f.help}
                {suggested && <Chip component="span" size="small" color="warning" variant="outlined" label="Suggested" sx={{ ml: 1, height: 18 }} />}
              </>
            );
            if (f.kind === "instruments") {
              const list = v as string[];
              return (
                <Grid key={f.key} item xs={12} sm={6} md={4}>
                  <Typography variant="caption" color="text.secondary">{f.label}</Typography>
                  <FormGroup row>
                    {["BANKNIFTY", "NIFTY"].map((sym) => (
                      <FormControlLabel
                        key={sym}
                        control={
                          <Checkbox
                            checked={list.includes(sym)}
                            onChange={(e) =>
                              setValues((s) => ({ ...s, [f.key]: e.target.checked ? [...list, sym] : list.filter((x) => x !== sym) }))
                            }
                          />
                        }
                        label={sym}
                      />
                    ))}
                  </FormGroup>
                  <Typography variant="caption" color={fe ? "error" : "text.secondary"}>{helper}</Typography>
                </Grid>
              );
            }
            if (f.kind === "bool") {
              return (
                <Grid key={f.key} item xs={12} sm={6} md={4}>
                  <FormControlLabel
                    control={<Switch checked={Boolean(v)} onChange={(e) => setValues((s) => ({ ...s, [f.key]: e.target.checked }))} />}
                    label={f.label}
                  />
                  <Typography variant="caption" color="text.secondary" display="block">{helper}</Typography>
                </Grid>
              );
            }
            return (
              <Grid key={f.key} item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  select={f.kind === "select"}
                  type={f.kind === "time" ? "time" : f.kind === "select" ? undefined : "number"}
                  label={f.label}
                  value={v}
                  onChange={(e) => setValues((s) => ({ ...s, [f.key]: e.target.value }))}
                  error={fe !== null}
                  helperText={helper}
                  InputLabelProps={f.kind === "time" ? { shrink: true } : undefined}
                  inputProps={{ "aria-label": f.label, step: f.kind === "int" ? 1 : "any" }}
                >
                  {f.options?.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </TextField>
              </Grid>
            );
          })}
        </Grid>
        {err && <Alert severity="error" sx={{ mt: 2 }} onClose={() => setErr(null)} data-testid={`error-${section.id}`}>{err}</Alert>}
        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Button variant="contained" disabled={invalid || saving || dirtyKeys.length === 0} onClick={() => void save()}>
            {saving ? "Saving…" : "Save"}
          </Button>
          <Button disabled={saving || dirtyKeys.length === 0} onClick={() => setValues(initial)}>Reset</Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

export const RULE_SET_V2 = "PRICE_DEMA100_WMA_V2";
export const RULE_SET_V6 = "PRICE_DEMA100_WMA_V6";

/** "09:15–12:00" or "09:15–12:00 and 13:30–15:00" (the optional second entry window). */
export function entryWindowsText(f: Record<string, DemaWmaFieldValue>): string {
  const w1 = `${f.entry_start}–${f.entry_end}`;
  return f.entry_start_2 && f.entry_end_2 ? `${w1} and ${f.entry_start_2}–${f.entry_end_2}` : w1;
}

export function rulesSummary(f: Record<string, DemaWmaFieldValue>, ruleSet?: string, config?: Record<string, unknown>): string[] {
  const idx = Array.isArray(f.instruments) ? f.instruments.join(" and ") : "—";
  const tf = Number(config?.timeframe_minutes ?? 5) === 1 ? 1 : 5;
  if (ruleSet === RULE_SET_V6) {
    const entry = (config?.entry ?? {}) as Record<string, unknown>;
    const exits = (config?.exits ?? {}) as Record<string, unknown>;
    const gap = typeof entry.min_dema_gap_points === "number" ? `${entry.min_dema_gap_points} index points` : "the configured gap";
    // optional per-version thresholds (e.g. V24): absent = the original v6 wording ("strictly rising / falling")
    const th = (entry.thresholds ?? null) as { gap_inclusive?: boolean; dema_slow_slope_min_points?: number; rsi_wma_slope_min_points?: number; rsi_wma_slope_max_points?: number | null } | null;
    const gapWord = th?.gap_inclusive ? "by at least" : "by more than";
    const dm = Number(th?.dema_slow_slope_min_points ?? 0);
    const wm = Number(th?.rsi_wma_slope_min_points ?? 0);
    // optional RSI-WMA spike cap (enhanced strategy): no entry when it moved more than this in one bar
    const cap = th?.rsi_wma_slope_max_points ?? null;
    const capText = cap !== null && cap !== undefined ? ` and by no more than ${cap} (spike cap)` : "";
    const exitMin = Number(exits.exit_wma_slope_min_points ?? 0);
    const rise = (line: string, m: number) => (m > 0 ? `${line} rose by more than ${m} since the previous bar` : `${line} is strictly rising`);
    const fall = (line: string, m: number) => (m > 0 ? `${line} fell by more than ${m} since the previous bar` : `${line} is strictly falling`);
    const sl = f.stop_loss_pct === null || f.stop_loss_pct === undefined ? "no stop-loss" : `stop-loss ${f.stop_loss_pct}% of premium`;
    const tgt = f.target_pct === null || f.target_pct === undefined ? "no target" : `target ${f.target_pct}% of premium`;
    const maxTrades = f.max_trades_per_day === null || f.max_trades_per_day === undefined ? "unlimited trades per day" : `at most ${f.max_trades_per_day} trades per day`;
    return [
      `v6 rule set (CE and PE). ${idx}, ${tf}-minute index bars, evaluated at bar close.`,
      `CE entry on ANY bar where all hold: DEMA(${f.dema_fast}) is above DEMA(${f.dema_slow}) ${gapWord} ${gap}; ${rise(`DEMA(${f.dema_slow})`, dm)}; the close is above DEMA(${f.dema_slow}) (a cross on this bar or already above); ${rise(`WMA(${f.rsi_wma}) of RSI(${f.rsi_period})`, wm)}${capText}.`,
      `PE entry on ANY bar where all hold: DEMA(${f.dema_slow}) is above DEMA(${f.dema_fast}) ${gapWord} ${gap}; ${fall(`DEMA(${f.dema_slow})`, dm)}; the close is below DEMA(${f.dema_slow}); ${fall(`WMA(${f.rsi_wma}) of RSI(${f.rsi_period})`, wm)}${capText}.`,
      `Slopes compare completed bars of the SAME session; a slope that disagrees on the bar is skipped. A bar right after a missing minute never opens a trade.`,
      `Re-entry: only after the open trade is closed, on a later bar that meets the entry conditions (no fresh cross needed).`,
      `Trade the 1-strike ITM option (CE ATM−step, PE ATM+step), nearest expiry (minimum ${f.min_dte} day(s) to expiry), ${f.lots} lot(s); ${maxTrades}.`,
      exits.exit_wma_source === "PRICE_WMA"
        ? exitMin > 0
          ? `Exit CE when WMA(${exits.exit_wma_length ?? 21}) of the index CLOSE falls by more than ${exitMin} points from the previous bar, PE when it rises by more than ${exitMin} (smaller moves hold; same session); an opposite signal only exits; ${sl}, ${tgt}.`
          : `Exit CE when WMA(${exits.exit_wma_length ?? 21}) of the index CLOSE turns strictly down, PE when it turns strictly up (flat holds; compared with the previous bar of the same session); an opposite signal only exits; ${sl}, ${tgt}.`
        : `Exit CE when WMA(${f.rsi_wma}) turns strictly down, PE when it turns strictly up (flat holds); an opposite signal only exits; ${sl}, ${tgt}.`,
      `Entries ${entryWindowsText(f)} IST; everything squared off at ${f.square_off}.`,
    ];
  }
  if (ruleSet === RULE_SET_V2) {
    const entry = (config?.entry ?? {}) as Record<string, unknown>;
    const gap = entry.min_dema_gap_points;
    const gapText = typeof gap === "number" ? `${gap} index points` : "the configured number of index points (per index)";
    const sl = f.stop_loss_pct === null || f.stop_loss_pct === undefined ? "no stop-loss" : `stop-loss ${f.stop_loss_pct}% of premium`;
    const tgt = f.target_pct === null || f.target_pct === undefined ? "no target" : `target ${f.target_pct}% of premium`;
    return [
      `v2 rule set (CE-only). ${idx}, ${tf}-minute index bars, evaluated at bar close.`,
      `CE entry on the FIRST bar where all of these hold (a new setup, not every qualifying candle): DEMA(${f.dema_fast}) is above DEMA(${f.dema_slow}) by more than ${gapText}; DEMA(${f.dema_slow}) is not falling; the close is above DEMA(${f.dema_slow}); WMA(${f.rsi_wma}) of RSI(${f.rsi_period}) is strictly rising.`,
      tf === 1
        ? `Slopes compare completed bars of the SAME session only; on 1-minute bars the first entry-eligible bar closes at the entry-window start (${f.entry_start}). A bar right after a missing minute never opens a trade. No PE trades.`
        : `Slopes compare completed bars of the SAME session only, so the first entry-eligible bar of a day closes at 09:25. No PE trades.`,
      `After any exit, re-entry needs a fresh close back above DEMA(${f.dema_slow}) and price staying above it since.`,
      `Trade the ATM call, nearest expiry (minimum ${f.min_dte} day(s) to expiry), ${f.lots} lot(s).`,
      `Exit when WMA(${f.rsi_wma}) turns strictly down (flat holds); ${sl}, ${tgt}.`,
      `Entries ${entryWindowsText(f)} IST; everything squared off at ${f.square_off}.`,
    ];
  }
  return [
    `${idx}, ${tf}-minute index bars, evaluated at bar close.`,
    `CE entry: DEMA(${f.dema_fast}) crosses above DEMA(${f.dema_slow}) and WMA(${f.rsi_wma}) of RSI(${f.rsi_period}) is rising on the same bar.`,
    `PE entry: DEMA(${f.dema_fast}) crosses below DEMA(${f.dema_slow}) and WMA(${f.rsi_wma}) of RSI(${f.rsi_period}) is falling on the same bar.`,
    `Trade the ATM option, nearest expiry (minimum ${f.min_dte} day(s) to expiry), ${f.lots} lot(s).`,
    `Exit CE when WMA(${f.rsi_wma}) turns down, PE when it turns up; stop-loss ${f.stop_loss_pct}% and target ${f.target_pct}% of premium.`,
    `Entries ${entryWindowsText(f)} IST; everything squared off at ${f.square_off}. Re-entry only after a new DEMA cross.`,
  ];
}

